import { useState, useRef, useEffect, useCallback } from 'react';
import {
  BrainCircuit, Send, Loader2, Copy, Check,
  AlertTriangle, ChevronRight, Sparkles,
} from 'lucide-react';
import { dealCoachChat, MavisApiError } from '../../lib/mavisApi';
import { captureEdgeFunctionError } from '../../lib/sentry';
import type { MavisDealCoachResponse } from '../../types/mavis';

// ── Constants ──────────────────────────────────────────────────────────────────

const STARTER_PROMPTS = [
  'What should I do next?',
  'What are the biggest risks in this deal?',
  'What should I ask on the next call?',
  'How can I improve the forecast?',
  'What is missing from MEDDPICC?',
  'How should I position against competitors?',
];

// ── Types ──────────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  response?: MavisDealCoachResponse;
  error?: string;
}

interface Props {
  opportunityId: string;
}

// Report a failed MAVIS call and turn it into the message shown to the user.
function toUserError(err: unknown): unknown {
  if (!(err instanceof MavisApiError)) return err;
  captureEdgeFunctionError('mavis-chat', err.status, JSON.stringify(err.body));
  return new Error(typeof err.body?.error === 'string' && err.body.error ? err.body.error : `Server error (${err.status})`);
}

// ── Confidence pill ────────────────────────────────────────────────────────────

function ConfidencePill({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, value));
  const cls = pct >= 70
    ? 'text-forest-600'
    : pct >= 40 ? 'text-amber-600' : 'text-red-500';
  return <span className={`text-[10px] font-medium ${cls}`}>Confidence {pct}%</span>;
}

// ── Assistant message card ─────────────────────────────────────────────────────

function AssistantCard({
  response,
  isCopied,
  onCopy,
}: {
  response: MavisDealCoachResponse;
  isCopied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="bg-app-surface rounded-2xl rounded-tl-md border border-app-line shadow-sm overflow-hidden">
      {/* Answer */}
      <div className="px-5 py-4">
        <p className="text-sm text-app-ink leading-relaxed">{response.answer}</p>
      </div>

      {/* Why it matters */}
      {response.why_it_matters && (
        <div className="px-5 py-3 border-t border-app-line bg-forest-50/50">
          <p className="text-[11px] font-semibold text-forest-700 uppercase tracking-wide mb-1">
            Why it matters
          </p>
          <p className="text-xs text-app-muted leading-relaxed">{response.why_it_matters}</p>
        </div>
      )}

      {/* Next steps */}
      {response.recommended_next_steps?.length > 0 && (
        <div className="px-5 py-3 border-t border-app-line">
          <p className="text-[11px] font-semibold text-app-muted uppercase tracking-wide mb-2">
            Next steps
          </p>
          <ol className="space-y-1.5">
            {response.recommended_next_steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-app-ink">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-forest-100 text-forest-700 text-[10px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Questions to ask */}
      {response.questions_to_ask?.length > 0 && (
        <div className="px-5 py-3 border-t border-app-line">
          <p className="text-[11px] font-semibold text-app-muted uppercase tracking-wide mb-2">
            Questions to ask
          </p>
          <ul className="space-y-1.5">
            {response.questions_to_ask.map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-app-muted">
                <ChevronRight className="w-3 h-3 flex-shrink-0 text-app-muted mt-0.5" />
                {q}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risks & assumptions */}
      {response.risks_and_assumptions?.length > 0 && (
        <div className="px-5 py-3 border-t border-app-line">
          <p className="text-[11px] font-semibold text-app-muted uppercase tracking-wide mb-2">
            Risks & assumptions
          </p>
          <ul className="space-y-1.5">
            {response.risks_and_assumptions.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-app-muted">
                <AlertTriangle className="w-3 h-3 flex-shrink-0 text-amber-400 mt-0.5" />
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Context gaps */}
      {response.context_gaps?.length > 0 && (
        <div className="px-5 py-3 border-t border-app-line bg-amber-50/40">
          <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-2">
            What MAVIS doesn't know
          </p>
          <ul className="space-y-1">
            {response.context_gaps.map((gap, i) => (
              <li key={i} className="text-xs text-amber-700 leading-relaxed">• {gap}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="px-5 py-2.5 border-t border-app-line flex items-center justify-between">
        <ConfidencePill value={response.confidence} />
        <button
          onClick={onCopy}
          className="flex items-center gap-1 text-[11px] font-medium text-app-muted hover:text-forest-600 transition-colors"
        >
          {isCopied ? (
            <><Check className="w-3 h-3 text-forest-500" /><span className="text-forest-600">Copied</span></>
          ) : (
            <><Copy className="w-3 h-3" />Copy answer</>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MavisDealCoachPanel({ opportunityId }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);

    try {
      // Send only the answer text for assistant turns to keep context lean
      const history = messages.slice(-6).map(m => ({
        role: m.role,
        content: m.role === 'assistant' && m.response ? m.response.answer : m.content,
      }));

      const data = await dealCoachChat(opportunityId, {
        message: trimmed,
        conversation_history: history,
      }).catch((err: unknown) => {
        throw toUserError(err);
      });

      if (!data.response) {
        throw new Error('No response from MAVIS. Please try again.');
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response.answer,
        response: data.response as MavisDealCoachResponse,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '',
        error: err instanceof Error ? err.message : 'Failed to get a response. Please try again.',
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, messages, opportunityId]);

  const copyAnswer = async (idx: number, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="flex flex-col gap-4">

      {/* ── Intro + starter prompts (shown when chat is empty) ── */}
      {isEmpty && (
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
          <div className="px-6 py-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-forest-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <BrainCircuit className="w-5 h-5 text-forest-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-app-ink">MAVIS Deal Coach</p>
                <p className="text-xs text-app-muted">Opportunity-scoped AI coaching</p>
              </div>
            </div>
            <p className="text-xs text-app-muted leading-relaxed mb-4">
              MAVIS has read your qualification data, stakeholders, account context, vendor intelligence,
              and your latest prep brief. Ask any deal-specific question and get grounded, practical answers.
            </p>
            <div className="flex flex-wrap gap-2">
              {STARTER_PROMPTS.map(prompt => (
                <button
                  key={prompt}
                  onClick={() => send(prompt)}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-forest-700 bg-forest-50 hover:bg-forest-100 border border-forest-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles className="w-3 h-3 flex-shrink-0" />
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Message list ── */}
      {messages.length > 0 && (
        <div className="space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx}>
              {msg.role === 'user' ? (
                <div className="flex justify-end">
                  <div className="max-w-[80%] px-4 py-2.5 bg-forest-600 text-app-surface rounded-2xl rounded-tr-md text-sm leading-relaxed">
                    {msg.content}
                  </div>
                </div>
              ) : msg.error ? (
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  </div>
                  <div className="flex-1 px-4 py-3 bg-red-50 border border-red-200 rounded-2xl rounded-tl-md">
                    <p className="text-xs text-red-700">{msg.error}</p>
                  </div>
                </div>
              ) : msg.response ? (
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 bg-forest-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <BrainCircuit className="w-4 h-4 text-forest-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <AssistantCard
                      response={msg.response}
                      isCopied={copiedIdx === idx}
                      onCopy={() => copyAnswer(idx, msg.response!.answer)}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ))}

          {/* Typing indicator */}
          {loading && (
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 bg-forest-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Loader2 className="w-4 h-4 text-forest-500 animate-spin" />
              </div>
              <div className="px-4 py-3 bg-app-surface border border-app-line rounded-2xl rounded-tl-md shadow-sm">
                <div className="flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 bg-forest-300 rounded-full animate-bounce [animation-delay:0ms]" />
                  <span className="w-1.5 h-1.5 bg-forest-300 rounded-full animate-bounce [animation-delay:150ms]" />
                  <span className="w-1.5 h-1.5 bg-forest-300 rounded-full animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            </div>
          )}

          {/* Compact starter prompts after first message */}
          {!loading && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {STARTER_PROMPTS.slice(0, 3).map(prompt => (
                <button
                  key={prompt}
                  onClick={() => send(prompt)}
                  className="px-2.5 py-1 text-[11px] font-medium text-app-muted bg-app-canvas hover:bg-app-canvas border border-app-line rounded-lg transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      )}

      {/* ── Input box ── */}
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm p-3 flex items-end gap-3 sticky bottom-4">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          placeholder="Ask MAVIS about this deal… (Enter to send, Shift+Enter for new line)"
          rows={2}
          className="flex-1 resize-none text-sm text-app-ink placeholder-gray-400 bg-transparent outline-none leading-relaxed disabled:opacity-50"
        />
        <button
          onClick={() => send(input)}
          disabled={!input.trim() || loading}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-forest-600 hover:bg-forest-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-colors"
        >
          {loading
            ? <Loader2 className="w-4 h-4 text-app-surface animate-spin" />
            : <Send className="w-4 h-4 text-app-surface" />
          }
        </button>
      </div>
    </div>
  );
}
